import { createSlice } from "@reduxjs/toolkit"
import { notification } from "antd"

import client from "../../lib/client"
import {
    clientBatchRunDownloadFilesUrl,
    clientBatchEligibleClientsUrl,
    clientBatchRunPreflightUrl,
    clientBatchRunsUrl,
    clientBatchRunBillsUrl,
    clientBatchRunExclusionsUrl,
    clientBatchRunUrl,
    clientBatchRunFinalizePlanUrl,
    clientBatchRunFinalizeUrl,
    clientBatchRunRecheckFlagsUrl,
    clientBatchSweepUrl,
    accountingBatchGenerateUploadFileUrl,
    accountingBatchGenerateInvoicePdfUrl,
    accountingBatchGenerateBillImagesZipUrl,
    clientBatchQuickbooksFilesUrl,
    clientBatchRegenerateQuickbooksFileUrl,
} from "../../lib/endpoints"

const emptyState = {
    eligibleClients: [],
    total: 0,
    openRuns: [],
    selectedClientIds: [],
    // { [clientId]: [batchGroupId, ...] } -- only for clients with SOME groups unticked. A client
    // absent from here sweeps all of its groups (A/R feedback 5b).
    selectedBatchGroupIds: {},
    run: null,
    swept: [],
    exceptions: [],
    totals: null,
    blocked: [],
    sweepMessage: null,
    loading: false,
    sweeping: false,

    // --- run review (step 5) ---
    runDetail: null,
    runBills: [],
    runBillsTotal: 0,
    runBillsState: "all",
    runTotals: null,
    runGroups: [],
    runExcludedBillIds: [],
    runBillsFilters: {},
    runBillsSort: { column: "client_name", direction: "asc" },
    runBillsPagination: { current: 1, pageSize: 50, total: 0 },
    runBillsLoading: false,
    pendingBillIds: [],

    // --- pre-flight + runs list (step 6) ---
    preflight: null,
    preflightRunId: null,
    preflightLoading: false,
    preflightFilter: null,
    todaysRuns: [],
    todaysRunsLoading: false,

    // --- QuickBooks daily file (step 8) ---
    quickbooksFiles: [],
    quickbooksFilesLoading: false,
    // file_date strings with a rebuild in flight, so the row can show "Generating…".
    quickbooksGenerating: [],

    // --- finalize (step 7) ---
    finalizePlan: null,
    finalizePlanLoading: false,
    // { "142-17": "142 APGN 20261007" } -- keyed by client and batch group, since one client can
    // have several batches in a run and they need separate codes.
    finalizeCodes: {},
    finalizeErrors: [],
    finalizing: false,
    finalizeResult: null,
    // "<batch id>:<kind>" for every file already asked for and not yet broadcast back, so Generate
    // missing files never starts a second build of one that is still running. The zip can take
    // minutes, which makes that window real. Seeded by finalize; reset when the page unmounts.
    requestedFiles: [],
}

const FILE_KINDS = ["upload", "pdf", "zip"]
// The API names each file's stale flag by the file, not by these short kinds.
const STALE_KEY = { upload: "upload_file", pdf: "invoice_pdf", zip: "bill_images_zip" }
// The job's broadcast says whether the data moved on while it was building (A/R feedback item 2).
const withStale = (batch, staleKey, stale) => ({ ...(batch.stale_files || {}), [staleKey]: !!stale })
const fileKey = (id, kind) => `${id}:${kind}`
const unrequest = (state, id, kind) => {
    state.requestedFiles = state.requestedFiles.filter((key) => key !== fileKey(id, kind))
}

export const clientBatchingSlice = createSlice({
    name: "clientBatching",
    initialState: emptyState,
    reducers: {
        setEligibleClients: (state, action) => {
            state.eligibleClients = action.payload.clients
            state.total = action.payload.total
            state.openRuns = action.payload.open_runs
        },
        setSelectedClientIds: (state, action) => {
            state.selectedClientIds = action.payload
            // A client no longer selected has no group selection to remember.
            Object.keys(state.selectedBatchGroupIds).forEach((clientId) => {
                if (!action.payload.includes(Number(clientId))) delete state.selectedBatchGroupIds[clientId]
            })
        },
        // ids: the ticked groups; null clears the entry (all groups).
        setClientBatchGroups: (state, action) => {
            const { clientId, ids } = action.payload
            if (ids === null) delete state.selectedBatchGroupIds[clientId]
            else state.selectedBatchGroupIds[clientId] = ids
        },
        setSweepResult: (state, action) => {
            state.run = action.payload.client_batch_run
            state.swept = action.payload.swept
            state.exceptions = action.payload.exceptions
            state.totals = action.payload.totals
            state.sweepMessage = action.payload.message
            state.blocked = []
        },
        setBlocked: (state, action) => {
            state.blocked = action.payload
        },
        clearSweep: (state) => {
            state.run = null
            state.swept = []
            state.exceptions = []
            state.totals = null
            state.blocked = []
            state.sweepMessage = null
        },
        setLoading: (state, action) => {
            state.loading = action.payload
        },
        setSweeping: (state, action) => {
            state.sweeping = action.payload
        },

        setRunBills: (state, action) => {
            state.runDetail = action.payload.run
            state.runBills = action.payload.bills
            state.runBillsTotal = action.payload.total
            state.runBillsState = action.payload.state
            state.runTotals = action.payload.totals ? action.payload.totals.run : null
            state.runGroups = action.payload.totals ? action.payload.totals.by_group : []
            state.runExcludedBillIds = action.payload.run ? action.payload.run.excluded_bill_ids || state.runExcludedBillIds : []
        },
        setRunExcludedBillIds: (state, action) => {
            state.runExcludedBillIds = action.payload
        },
        setRunBillsLoading: (state, action) => {
            state.runBillsLoading = action.payload
        },
        setRunBillsFilters: (state, action) => {
            state.runBillsFilters = action.payload
        },
        setRunBillsSort: (state, action) => {
            state.runBillsSort = action.payload
        },
        setRunBillsPagination: (state, action) => {
            state.runBillsPagination = action.payload
        },
        setRunBillsState: (state, action) => {
            state.runBillsState = action.payload
        },
        markExclusionPending: (state, action) => {
            state.pendingBillIds = state.pendingBillIds.concat(action.payload)
        },
        clearExclusionPending: (state, action) => {
            state.pendingBillIds = state.pendingBillIds.filter((id) => !action.payload.includes(id))
        },
        // The server returns the FULL excluded array, so a client that lost a race converges on
        // the server's view rather than drifting.
        applyExclusionResult: (state, action) => {
            state.runExcludedBillIds = action.payload.excluded_bill_ids
            state.runBills = state.runBills.map((bill) => ({
                ...bill,
                excluded: action.payload.excluded_bill_ids.includes(bill.id),
            }))
            // Replace wholesale rather than merging: a group whose bills are all excluded drops
            // out of the totals, and re-including one has to bring it back. Merging into the
            // existing list can do neither.
            if (action.payload.totals) {
                state.runTotals = action.payload.totals.run
                state.runGroups = action.payload.totals.by_group
            } else if (action.payload.groups) {
                state.runGroups = action.payload.groups
            }
            if (action.payload.counts && state.runDetail) {
                state.runDetail = { ...state.runDetail, ...{
                    included_count: action.payload.counts.included,
                    excluded_count: action.payload.counts.excluded,
                } }
            }
        },
        setPreflight: (state, action) => {
            state.preflight = action.payload.data
            state.preflightRunId = action.payload.runId
        },
        setPreflightLoading: (state, action) => {
            state.preflightLoading = action.payload
        },
        setPreflightFilter: (state, action) => {
            state.preflightFilter = action.payload
        },
        setTodaysRuns: (state, action) => {
            state.todaysRuns = action.payload.runs
        },
        setTodaysRunsLoading: (state, action) => {
            state.todaysRunsLoading = action.payload
        },
        setFinalizePlan: (state, action) => {
            state.finalizePlan = action.payload
            // Seed the inputs from the server's suggestions, but never clobber something the
            // reviewer has already typed -- a re-fetch after a 409 must not undo their edits.
            ;(action.payload.groups || []).forEach((group) => {
                const key = `${group.client_id}-${group.batch_group_id || "none"}`
                if (state.finalizeCodes[key] === undefined) {
                    state.finalizeCodes[key] = group.suggested_batch_code || ""
                }
            })
        },
        setFinalizePlanLoading: (state, action) => {
            state.finalizePlanLoading = action.payload
        },
        setFinalizeCode: (state, action) => {
            state.finalizeCodes[action.payload.key] = action.payload.value
        },
        setFinalizeErrors: (state, action) => {
            state.finalizeErrors = action.payload
        },
        setFinalizing: (state, action) => {
            state.finalizing = action.payload
        },
        setFinalizeResult: (state, action) => {
            state.finalizeResult = action.payload
        },
        // The job broadcasts over ActionCable when a file finishes, so the result screen can fill
        // in without polling.
        updateBatchUploadFile: (state, action) => {
            const { accounting_batch_id: id, link } = action.payload
            unrequest(state, id, "upload")
            const apply = (batches) =>
                (batches || []).map((batch) =>
                    batch.id === id
                        ? { ...batch, upload_file_url: link, needs_upload_file: false, stale_files: withStale(batch, "upload_file", action.payload.stale) }
                        : batch
                )
            if (state.finalizeResult) {
                state.finalizeResult.accounting_batches = apply(state.finalizeResult.accounting_batches)
            }
            if (state.finalizePlan) {
                state.finalizePlan.accounting_batches = apply(state.finalizePlan.accounting_batches)
            }
        },
        // Same, for the invoice PDF. A failed job broadcasts with `error` and no link -- the row
        // stays "Generating…" so Generate missing files still offers it.
        updateBatchInvoicePdf: (state, action) => {
            const { accounting_batch_id: id, link, error } = action.payload
            unrequest(state, id, "pdf")
            if (error) return
            const apply = (batches) =>
                (batches || []).map((batch) =>
                    batch.id === id
                        ? { ...batch, invoice_pdf_url: link, needs_invoice_pdf: false, stale_files: withStale(batch, "invoice_pdf", action.payload.stale) }
                        : batch
                )
            if (state.finalizeResult) {
                state.finalizeResult.accounting_batches = apply(state.finalizeResult.accounting_batches)
            }
            if (state.finalizePlan) {
                state.finalizePlan.accounting_batches = apply(state.finalizePlan.accounting_batches)
            }
        },
        setQuickbooksFiles: (state, action) => {
            state.quickbooksFiles = action.payload
        },
        setQuickbooksFilesLoading: (state, action) => {
            state.quickbooksFilesLoading = action.payload
        },
        startQuickbooksFile: (state, action) => {
            if (!state.quickbooksGenerating.includes(action.payload)) state.quickbooksGenerating.push(action.payload)
        },
        // The job's broadcast. On error only the spinner clears; the row keeps its last good file.
        quickbooksFileUpdated: (state, action) => {
            const { file_date: date, error, link, file_name: fileName, batch_count: count, total, generated_at: at } =
                action.payload
            state.quickbooksGenerating = state.quickbooksGenerating.filter((d) => d !== date)
            if (error) return
            const apply = (file) =>
                file && file.file_date === date
                    ? {
                          ...file,
                          file_name: fileName || null,
                          batch_file_url: link || null,
                          generated_at: at || null,
                          file_batch_count: count,
                          file_total: total,
                          batch_count: count,
                          total,
                          missing: !link,
                          stale: false,
                      }
                    : file
            if (state.quickbooksFiles.some((file) => file.file_date === date)) {
                state.quickbooksFiles = state.quickbooksFiles.map(apply)
            } else if (link) {
                // A day the card has not seen yet, e.g. finalized in another tab: newest first.
                state.quickbooksFiles = [apply({ file_date: date }), ...state.quickbooksFiles].sort((a, b) =>
                    b.file_date.localeCompare(a.file_date)
                )
            }
            if (state.finalizePlan && state.finalizePlan.quickbooks_file) {
                state.finalizePlan.quickbooks_file = apply(state.finalizePlan.quickbooks_file)
            }
        },
        // Same, for the bill images zip. Its URL arrives as `zip_url`, not `link` (see cable.js).
        updateBatchBillImagesZip: (state, action) => {
            const { accounting_batch_id: id, zip_url: zipUrl, error } = action.payload
            unrequest(state, id, "zip")
            if (error) return
            const apply = (batches) =>
                (batches || []).map((batch) =>
                    batch.id === id
                        ? {
                              ...batch,
                              bill_images_zip_url: zipUrl,
                              needs_bill_images_zip: false,
                              stale_files: withStale(batch, "bill_images_zip", action.payload.stale),
                          }
                        : batch
                )
            if (state.finalizeResult) {
                state.finalizeResult.accounting_batches = apply(state.finalizeResult.accounting_batches)
            }
            if (state.finalizePlan) {
                state.finalizePlan.accounting_batches = apply(state.finalizePlan.accounting_batches)
            }
        },
        clearFilesRequested: (state, action) => {
            state.requestedFiles = state.requestedFiles.filter((key) => !action.payload.includes(key))
        },
        markFilesRequested: (state, action) => {
            action.payload.forEach((key) => {
                if (!state.requestedFiles.includes(key)) state.requestedFiles.push(key)
            })
        },
        clearRunReview: (state, action) => {
            const payload = action.payload || {}
            // The findings survive the review page's MOUNT for the same run only, so the banner can
            // step to the next finding. Another run's findings must never show here, and on unmount
            // they go -- the Pre-Flight page re-checks every time it opens anyway.
            const sameRun = payload.runId && state.preflightRunId === Number(payload.runId)
            if (!(sameRun && payload.keepPreflightFilter)) {
                state.preflight = null
                state.preflightRunId = null
            }
            // On MOUNT the review page keeps it: applyPreflightFilter sets the filter and then
            // routes here, so clearing it unconditionally threw away the thing the user clicked.
            // On unmount there is no payload and the filter goes with everything else.
            if (!payload.keepPreflightFilter) state.preflightFilter = null
            state.runDetail = null
            state.runBills = []
            state.runBillsTotal = 0
            state.runTotals = null
            state.runGroups = []
            state.runExcludedBillIds = []
            state.runBillsFilters = {}
            state.pendingBillIds = []
            state.finalizePlan = null
            state.finalizeCodes = {}
            state.finalizeErrors = []
            state.finalizeResult = null
            state.requestedFiles = []
            // The Flagged tab fixes the state to red_flagged; it must not carry into Review Bills.
            state.runBillsState = "all"
        },
    },
})

export const {
    setEligibleClients,
    setSelectedClientIds,
    setClientBatchGroups,
    setSweepResult,
    setBlocked,
    clearSweep,
    setLoading,
    setSweeping,
    setRunBills,
    setRunExcludedBillIds,
    setRunBillsLoading,
    setRunBillsFilters,
    setRunBillsSort,
    setRunBillsPagination,
    setRunBillsState,
    markExclusionPending,
    clearExclusionPending,
    applyExclusionResult,
    clearRunReview,
    setFinalizePlan,
    setFinalizePlanLoading,
    setFinalizeCode,
    setFinalizeErrors,
    setFinalizing,
    setFinalizeResult,
    updateBatchUploadFile,
    updateBatchInvoicePdf,
    updateBatchBillImagesZip,
    markFilesRequested,
    clearFilesRequested,
    setPreflight,
    setPreflightLoading,
    setPreflightFilter,
    setTodaysRuns,
    setTodaysRunsLoading,
    setQuickbooksFiles,
    setQuickbooksFilesLoading,
    startQuickbooksFile,
    quickbooksFileUpdated,
} = clientBatchingSlice.actions

export function fetchEligibleClients() {
    return async (dispatch) => {
        dispatch(setLoading(true))
        const response = await client.get(clientBatchEligibleClientsUrl).catch((e) => {
            dispatch(setLoading(false))
            return { error: e }
        })
        if (response && response.data && response.data.clients) {
            dispatch(setEligibleClients(response.data))
        }
        dispatch(setLoading(false))
        return response
    }
}

// The sweep is synchronous on the API: it reads bills and writes one run row, so the result comes
// back in the response rather than over the cable.
//
// try/catch rather than .catch(): lib/client rejects on any non-2xx, so a 409 (a client is already
// held by another open run) arrives as a thrown error, never as data.success === false.
export function sweepClients(clientIds, batchGroupIds = {}) {
    return async (dispatch) => {
        dispatch(setSweeping(true))
        dispatch(clearSweep())
        try {
            const payload = { client_ids: clientIds }
            // Only clients with a partial selection; the rest sweep every group, as before.
            const partial = Object.entries(batchGroupIds).filter(([clientId]) => clientIds.includes(Number(clientId)))
            if (partial.length) payload.batch_group_ids = Object.fromEntries(partial)
            const response = await client.post(clientBatchSweepUrl, payload)
            dispatch(setSweepResult(response.data))
            return response
        } catch (e) {
            if (e.response && e.response.status === 409) {
                dispatch(setBlocked(e.response.data.blocked || []))
                // Somebody took the client while this page was open; refresh so the row shows it.
                dispatch(fetchEligibleClients())
            }
            return { error: e }
        } finally {
            dispatch(setSweeping(false))
        }
    }
}

export function discardRun(runId) {
    return async (dispatch) => {
        const response = await client.delete(clientBatchRunUrl(runId)).catch((e) => ({ error: e }))
        dispatch(clearSweep())
        dispatch(setSelectedClientIds([]))
        dispatch(fetchEligibleClients())
        return response
    }
}

// --- run review -------------------------------------------------------------------------------

function buildRunBillsUrl(getState, overrides = {}) {
    const state = getState().clientBatching
    const runId = overrides.runId || (state.runDetail && state.runDetail.id)
    const filters = overrides.filters || state.runBillsFilters
    const sort = overrides.sort || state.runBillsSort
    // Merged, not replaced: ColumnSearchFilter dispatches `pagination: { current: 1 }` with no
    // pageSize, so taking its object wholesale would send per=undefined and return an empty page.
    const pagination = { ...state.runBillsPagination, ...(overrides.pagination || {}) }
    const billState = overrides.state || state.runBillsState

    let url = `${clientBatchRunBillsUrl(runId)}?page=${pagination.current}&per=${pagination.pageSize}`
    url += `&state=${encodeURIComponent(billState)}`
    // A pre-flight finding narrows the grid to its bills. Sent as two short params rather than a
    // list of ids, so the URL stays small however many bills the finding covers.
    const preflightFilter = overrides.preflightFilter !== undefined ? overrides.preflightFilter : state.preflightFilter
    if (preflightFilter) {
        url += `&preflight[code]=${encodeURIComponent(preflightFilter.code)}`
        url += `&preflight[key]=${encodeURIComponent(preflightFilter.key)}`
        // A finding is (client, code, key): several keys are shared by every client.
        if (preflightFilter.client_id) url += `&preflight[client_id]=${encodeURIComponent(preflightFilter.client_id)}`
    }
    if (sort && sort.column) {
        url += `&q[s]=${encodeURIComponent(`${sort.column} ${sort.direction}`)}`
    }
    Object.entries(filters || {}).forEach(([key, value]) => {
        // createFilterUrl does not encode its values; a filter containing & or # would corrupt the
        // query string, so this builds the URL itself -- including the date reformat that helper
        // does. ColumnDateFilter emits MM/DD/YYYY, which Rails casts in the wrong order and
        // silently matches nothing.
        if (value === undefined || value === null || value === "") return

        let sendValue = value
        if ((key.includes("gteq") || key.includes("lteq")) && typeof value === "string" && value.indexOf("/") >= 0) {
            const [month, day, year] = value.split("/")
            sendValue = `${year}-${month}-${day}`
        }
        url += `&q[${key}]=${encodeURIComponent(sendValue)}`
    })
    return url
}

// "N bill(s) moved back into the run · N newly red-flagged", or "" when nothing moved.
export function flagRecheckSummary(result) {
    if (!result || result.error) return ""
    const parts = []
    if (result.cleared_count) parts.push(`${result.cleared_count} bill(s) moved back into the run`)
    if (result.newly_flagged_count) parts.push(`${result.newly_flagged_count} newly red-flagged`)
    return parts.join(" · ")
}

// Moves bills between the run's Flagged tab and its swept set as red flags clear or appear (A/R
// feedback item 1). Returns the API's counts, or { error } -- callers decide whether to toast.
export function recheckFlags(runId) {
    return async () => {
        const response = await client.post(clientBatchRunRecheckFlagsUrl(runId)).catch((e) => ({ error: e }))
        return response && response.data ? response.data : { error: true }
    }
}

// The Finalize page's "Download all": the API zips every upload file (kind "upload_file") or every
// invoice PDF ("invoice_pdf") of the run. Same blob-and-anchor download as adminReportsSlice. The
// filename is built here: CORS does not expose Content-Disposition to this origin. On failure the
// body is a Blob, not JSON, so only the status is used.
const DOWNLOAD_ZIP_NAMES = { upload_file: "upload_files", invoice_pdf: "invoices" }

export function downloadRunFiles(runId, kind) {
    return async () => {
        try {
            const response = await client.get(clientBatchRunDownloadFilesUrl(runId, kind), { responseType: "blob" })
            const url = window.URL.createObjectURL(response.data)
            const anchor = document.createElement("a")
            anchor.href = url
            anchor.download = `run_${runId}_${DOWNLOAD_ZIP_NAMES[kind] || kind}.zip`
            document.body.appendChild(anchor) // Firefox needs it in the DOM
            anchor.click()
            document.body.removeChild(anchor)
            window.URL.revokeObjectURL(url)
            return { ok: true }
        } catch (e) {
            const status = e.response && e.response.status
            notification.error({
                message: status === 422 ? "No files generated yet" : "Download failed",
                description: status === 422 ? "None of these files have finished generating." : "Please try again.",
            })
            return { error: e }
        }
    }
}

export function fetchRunBills(runId, overrides = {}) {
    return async (dispatch, getState) => {
        dispatch(setRunBillsLoading(true))
        const url = buildRunBillsUrl(getState, { ...overrides, runId })
        const response = await client.get(url).catch((e) => ({ error: e }))
        if (response && response.data && response.data.bills) {
            dispatch(setRunBills(response.data))
            // Not for a header-only fetch: RunPreflightPage asks for pageSize 1 because it wants
            // the summary and no rows, and persisting that leaves the review grid on one row a
            // page for the rest of the session.
            if (overrides.pagination && overrides.persistPagination !== false) {
                dispatch(setRunBillsPagination(overrides.pagination))
            }
        }
        dispatch(setRunBillsLoading(false))
        return response
    }
}

// Signature matches what ColumnSearchFilter / ColumnDateFilter dispatch: an object of
// { filters, pagination, sort }. The run id comes from state -- this route has no :clientId, and
// sending client_id would trip AuthenticatedController#verify_client.
export function filterRunBills(query) {
    return async (dispatch, getState) => {
        const state = getState().clientBatching
        const pagination = { ...state.runBillsPagination, ...(query.pagination || {}) }

        if (query.filters) dispatch(setRunBillsFilters(query.filters))
        if (query.sort) dispatch(setRunBillsSort(query.sort))
        dispatch(setRunBillsPagination(pagination))
        return dispatch(fetchRunBills(null, { ...query, pagination }))
    }
}

export function setRunBillsStateFilter(billState) {
    return async (dispatch, getState) => {
        dispatch(setRunBillsState(billState))
        const pagination = { ...getState().clientBatching.runBillsPagination, current: 1 }
        dispatch(setRunBillsPagination(pagination))
        return dispatch(fetchRunBills(null, { state: billState, pagination }))
    }
}

export function toggleBillExclusion(billIds, excluded) {
    return async (dispatch, getState) => {
        const ids = Array.isArray(billIds) ? billIds : [billIds]
        const runId = getState().clientBatching.runDetail.id
        dispatch(markExclusionPending(ids))
        try {
            const response = await client.patch(clientBatchRunExclusionsUrl(runId), {
                bill_ids: ids,
                excluded: excluded,
            })
            dispatch(applyExclusionResult(response.data))
            return response
        } catch (e) {
            return { error: e }
        } finally {
            dispatch(clearExclusionPending(ids))
        }
    }
}

export function fetchPreflight(runId, { force = false } = {}) {
    return async (dispatch, getState) => {
        const state = getState().clientBatching
        // Both the review and pre-flight tabs ask for this; skip the round trip when it is the
        // same run and nothing has changed.
        if (!force && state.preflightRunId === Number(runId) && state.preflight) return { data: state.preflight }

        dispatch(setPreflightLoading(true))
        const response = await client.get(clientBatchRunPreflightUrl(runId)).catch((e) => ({ error: e }))
        if (response && response.data && response.data.summary) {
            dispatch(setPreflight({ data: response.data, runId: Number(runId) }))
        }
        dispatch(setPreflightLoading(false))
        return response
    }
}

export function fetchTodaysRuns() {
    return async (dispatch) => {
        dispatch(setTodaysRunsLoading(true))
        const response = await client.get(clientBatchRunsUrl).catch((e) => ({ error: e }))
        if (response && response.data && response.data.runs) dispatch(setTodaysRuns(response.data))
        dispatch(setTodaysRunsLoading(false))
        return response
    }
}

export function fetchQuickbooksFiles() {
    return async (dispatch) => {
        dispatch(setQuickbooksFilesLoading(true))
        const response = await client.get(clientBatchQuickbooksFilesUrl).catch((e) => ({ error: e }))
        if (response && response.data && response.data.quickbooks_files) {
            dispatch(setQuickbooksFiles(response.data.quickbooks_files))
        }
        dispatch(setQuickbooksFilesLoading(false))
        return response
    }
}

// The file arrives over ActionCable when the job finishes (quickbooksFileUpdated).
export function regenerateQuickbooksFile(fileDate) {
    return async (dispatch, getState) => {
        dispatch(startQuickbooksFile(fileDate))
        const response = await client
            .post(clientBatchRegenerateQuickbooksFileUrl, { file_date: fileDate, connection_id: getState().app.uid })
            .catch((e) => ({ error: e }))
        if (response.error) dispatch(quickbooksFileUpdated({ file_date: fileDate, error: true }))
        return response
    }
}

const sameFinding = (finding, filter) =>
    finding.reason === filter.code &&
    finding.key === filter.key &&
    (!filter.client_id || Number(finding.client_id) === Number(filter.client_id))

// Where the active finding filter sits among the findings that have bills, for the banner's
// "Finding 2 of 5" and Next link. null when there is nothing to step through -- no filter, the
// findings belong to another run, or the finding has since disappeared.
export const selectPreflightFindingPosition = (state) => {
    const { preflight, preflightRunId, preflightFilter, runDetail } = state.clientBatching
    if (!preflight || !preflightFilter || !runDetail || preflightRunId !== Number(runDetail.id)) return null
    const withBills = (preflight.findings || []).filter((finding) => finding.bill_count > 0)
    const index = withBills.findIndex((finding) => sameFinding(finding, preflightFilter))
    if (index < 0) return null
    return { index, total: withBills.length, current: withBills[index], next: withBills[index + 1] || null }
}

export function applyPreflightFilter(finding) {
    return async (dispatch, getState) => {
        const filter = finding
            ? { code: finding.reason, key: finding.key, client_id: finding.client_id, message: finding.message }
            : null
        dispatch(setPreflightFilter(filter))
        const pagination = { ...getState().clientBatching.runBillsPagination, current: 1 }
        dispatch(setRunBillsPagination(pagination))
        return dispatch(fetchRunBills(null, { preflightFilter: filter, pagination }))
    }
}

export const selectEligibleClients = (state) => state.clientBatching.eligibleClients
export const selectTotal = (state) => state.clientBatching.total
export const selectOpenRuns = (state) => state.clientBatching.openRuns
export const selectSelectedClientIds = (state) => state.clientBatching.selectedClientIds
export const selectRun = (state) => state.clientBatching.run
export const selectSwept = (state) => state.clientBatching.swept
export const selectExceptions = (state) => state.clientBatching.exceptions
export const selectTotals = (state) => state.clientBatching.totals
export const selectBlocked = (state) => state.clientBatching.blocked
export const selectSweepMessage = (state) => state.clientBatching.sweepMessage
export const selectLoading = (state) => state.clientBatching.loading
export const selectSweeping = (state) => state.clientBatching.sweeping

export const selectRunDetail = (state) => state.clientBatching.runDetail
export const selectRunBills = (state) => state.clientBatching.runBills
export const selectRunBillsTotal = (state) => state.clientBatching.runBillsTotal
export const selectRunBillsState = (state) => state.clientBatching.runBillsState
export const selectRunTotals = (state) => state.clientBatching.runTotals
export const selectRunGroups = (state) => state.clientBatching.runGroups
export const selectRunExcludedBillIds = (state) => state.clientBatching.runExcludedBillIds
export const selectRunBillsFilters = (state) => state.clientBatching.runBillsFilters
export const selectRunBillsSort = (state) => state.clientBatching.runBillsSort
export const selectRunBillsPagination = (state) => state.clientBatching.runBillsPagination
export const selectRunBillsLoading = (state) => state.clientBatching.runBillsLoading
export const selectPendingBillIds = (state) => state.clientBatching.pendingBillIds
export const selectQuickbooksFiles = (state) => state.clientBatching.quickbooksFiles
export const selectQuickbooksFilesLoading = (state) => state.clientBatching.quickbooksFilesLoading
export const selectQuickbooksGenerating = (state) => state.clientBatching.quickbooksGenerating


// --- finalize (step 7) -------------------------------------------------------------------------

export const finalizeCodeKey = (group) => `${group.client_id}-${group.batch_group_id || "none"}`

export function fetchFinalizePlan(runId) {
    return async (dispatch) => {
        dispatch(setFinalizePlanLoading(true))
        const response = await client.get(clientBatchRunFinalizePlanUrl(runId)).catch((e) => ({ error: e }))
        if (response && response.data) dispatch(setFinalizePlan(response.data))
        dispatch(setFinalizePlanLoading(false))
        return response
    }
}

// try/catch rather than .catch(): lib/client rejects every non-2xx, and this endpoint signals with
// 409 ("the world moved") and 422 ("your input is wrong") rather than with success:false.
export function finalizeRun(runId, { acknowledgeDrift = false, acknowledgeWillFail = false } = {}) {
    return async (dispatch, getState) => {
        const state = getState().clientBatching
        const groups = (state.finalizePlan && state.finalizePlan.groups) || []
        const batches = groups.map((group) => ({
            client_id: group.client_id,
            batch_group_id: group.batch_group_id,
            batch_code: state.finalizeCodes[finalizeCodeKey(group)],
        }))

        dispatch(setFinalizing(true))
        dispatch(setFinalizeErrors([]))
        try {
            const response = await client.post(clientBatchRunFinalizeUrl(runId), {
                batches,
                batch_date: state.finalizePlan && state.finalizePlan.batch_date,
                acknowledge_drift: acknowledgeDrift,
                acknowledge_will_fail: acknowledgeWillFail,
                connection_id: getState().app.uid,
            })
            dispatch(setFinalizeResult(response.data))
            // Finalize has just enqueued every file for every batch.
            dispatch(
                markFilesRequested(
                    (response.data.accounting_batches || []).flatMap((batch) =>
                        FILE_KINDS.map((kind) => fileKey(batch.id, kind))
                    )
                )
            )
            // Re-read so the screen flips to the finalized/result view from the server's own state
            // rather than from the POST response alone.
            await dispatch(fetchFinalizePlan(runId))
            return response
        } catch (e) {
            const data = (e.response && e.response.data) || {}
            if (data.errors) dispatch(setFinalizeErrors(data.errors))
            // 409 means the plan itself is stale, so re-read it; the reviewer's typed codes survive
            // because setFinalizePlan never overwrites an existing entry.
            // A bill became red-flagged since the sweep: move it to the Flagged tab first, so the
            // reloaded plan no longer contains it.
            if (data.code === "flagged_at_finalize") await dispatch(recheckFlags(runId))
            if (e.response && e.response.status === 409) await dispatch(fetchFinalizePlan(runId))
            return { error: e, data }
        } finally {
            dispatch(setFinalizing(false))
        }
    }
}

// Re-enqueues generation for batches that have no file. Same endpoint the per-client Accounting
// Batches screen uses, so a lost :async job is recoverable from here instead of client by client.
// Each batch can be missing any of its three files, so the request list is per FILE, and the counts
// returned are files, not batches. Files already requested and not yet back are skipped.
const FILE_URL_FOR = {
    upload: accountingBatchGenerateUploadFileUrl,
    pdf: accountingBatchGenerateInvoicePdfUrl,
    zip: accountingBatchGenerateBillImagesZipUrl,
}

// Every file of every batch for which `wanted(batch, kind)` holds, minus those already requested.
function filesWhere(batches, requestedFiles, wanted) {
    const files = []
    ;(batches || []).forEach((batch) =>
        FILE_KINDS.forEach((kind) => {
            const key = fileKey(batch.id, kind)
            if (wanted(batch, kind) && !requestedFiles.includes(key)) {
                files.push({ key, url: FILE_URL_FOR[kind](batch.id, batch.client_id) })
            }
        })
    )
    return files
}

export function missingFiles(batches, requestedFiles) {
    const needs = { upload: "needs_upload_file", pdf: "needs_invoice_pdf", zip: "needs_bill_images_zip" }
    return filesWhere(batches, requestedFiles, (batch, kind) => batch[needs[kind]])
}

// Files built from data that has since changed (A/R feedback item 2).
export function staleFiles(batches, requestedFiles) {
    return filesWhere(batches, requestedFiles, (batch, kind) => !!(batch.stale_files && batch.stale_files[STALE_KEY[kind]]))
}

export function generateMissingFiles(batches) {
    return requestFiles((state) => missingFiles(batches, state.clientBatching.requestedFiles))
}

export function regenerateStaleFiles(batches) {
    return requestFiles((state) => staleFiles(batches, state.clientBatching.requestedFiles))
}

function requestFiles(pick) {
    return async (dispatch, getState) => {
        const uid = getState().app.uid
        const files = pick(getState())
        dispatch(markFilesRequested(files.map((file) => file.key)))
        const results = await Promise.all(
            files.map((file) => client.get(`${file.url}&connection_id=${uid}`).catch((e) => ({ error: e })))
        )
        // A request that failed will never broadcast, so it must not stay marked as requested.
        const failedKeys = files.filter((file, i) => results[i] && results[i].error).map((file) => file.key)
        if (failedKeys.length) dispatch(clearFilesRequested(failedKeys))
        return { requested: files.length, failed: failedKeys.length }
    }
}

export const selectPreflight = (state) => state.clientBatching.preflight
export const selectPreflightSummary = (state) => state.clientBatching.preflight && state.clientBatching.preflight.summary
export const selectPreflightLoading = (state) => state.clientBatching.preflightLoading
export const selectPreflightFilter = (state) => state.clientBatching.preflightFilter
export const selectTodaysRuns = (state) => state.clientBatching.todaysRuns
export const selectTodaysRunsLoading = (state) => state.clientBatching.todaysRunsLoading

export const selectFinalizePlan = (state) => state.clientBatching.finalizePlan
export const selectFinalizePlanLoading = (state) => state.clientBatching.finalizePlanLoading
export const selectFinalizeCodes = (state) => state.clientBatching.finalizeCodes
export const selectFinalizeErrors = (state) => state.clientBatching.finalizeErrors
export const selectFinalizing = (state) => state.clientBatching.finalizing
export const selectFinalizeResult = (state) => state.clientBatching.finalizeResult
export const selectRequestedFiles = (state) => state.clientBatching.requestedFiles
export const selectSelectedBatchGroupIds = (state) => state.clientBatching.selectedBatchGroupIds

export default clientBatchingSlice.reducer
